import React, { useState, useEffect, useMemo } from 'react';
import { fetchDataFromApi } from '../../api/googleSheet';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Timeline from './Timeline';

const Dashboard = ({ availableYears }) => {
    const [dashboardYear, setDashboardYear] = useState(new Date().getFullYear());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDashboardData = async () => {
            setLoading(true);
            try {
                const yearsToFetch = dashboardYear === 'all' ? availableYears : [dashboardYear];
                
                if (yearsToFetch.length === 0 && dashboardYear !== 'all') {
                    yearsToFetch.push(dashboardYear);
                }
                
                if (yearsToFetch.length === 0) {
                    setEvents([]);
                    return;
                }

                const promises = yearsToFetch.map(year =>
                    fetchDataFromApi(["ID", "Event", "Date", "Location", "Prize", "Status"], `Event${year + 543}`)
                );
                const results = await Promise.all(promises);
                const combinedData = results.flat().filter(item => item && item.ID);
                setEvents(combinedData);
            } catch (error) {
                console.error("Failed to load dashboard data", error);
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };
        loadDashboardData();
    }, [dashboardYear, availableYears]);

    const dashboardStats = useMemo(() => {
        if (events.length === 0) {
            return { totalEvents: 0, statusData: [], prizeData: [] };
        }

        const statusCounts = events.reduce((acc, event) => {
            const status = event.Status || "ไม่ระบุ";
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

        const prizeCounts = events.reduce((acc, event) => {
            const prize = event.Prize || "ไม่มี";
            const nonPrizeKeywords = ["ไม่มี", "ยังไม่แข่งขัน", "N/A", ""];
            if (prize && !nonPrizeKeywords.includes(prize.trim())) {
               acc[prize] = (acc[prize] || 0) + 1;
            }
            return acc;
        }, {});
        
        const prizeData = Object.entries(prizeCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([name, count]) => ({ name, จำนวน: count }));


        return {
            totalEvents: events.length,
            statusData,
            prizeData,
        };
    }, [events]);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF4560'];

    if (loading) return <div className="flex justify-center items-center h-96"><div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div></div>;

    return (
        <div>
            <header className="mb-8 text-center">
                <h1 className="text-4xl font-extrabold text-gray-800 tracking-tight">Dashboard</h1>
                <div className="flex justify-center items-center gap-4 mt-2">
                     <p className="text-lg text-gray-500">ภาพรวมข้อมูลการแข่งขัน</p>
                     <select
                        value={dashboardYear}
                        onChange={e => setDashboardYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                        className="border-gray-300 rounded-md text-base p-2 bg-white shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">ทุกปี</option>
                        {availableYears.map(year => (
                            <option key={`dashboard-year-${year}`} value={year}>ปี {year}</option>
                        ))}
                    </select>
                </div>
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col justify-center items-center">
                    <h2 className="text-lg font-semibold text-gray-500">จำนวนงานวิ่งทั้งหมด</h2>
                    <p className="text-5xl font-extrabold text-gray-800 mt-2">{dashboardStats.totalEvents}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-lg font-semibold text-gray-700 mb-4 text-center">สรุปตามสถานะ</h2>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie data={dashboardStats.statusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {dashboardStats.statusData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="mt-6 bg-white p-6 rounded-xl shadow-lg">
                 <h2 className="text-lg font-semibold text-gray-700 mb-4 text-center">5 อันดับรางวัลสูงสุด</h2>
                 <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dashboardStats.prizeData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={150} tick={{fontSize: 12}}/>
                        <Tooltip />
                        <Bar dataKey="จำนวน">
                            {dashboardStats.prizeData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <Timeline events={events} />
        </div>
    );
};

export default Dashboard;
